import api from './api';
import { ApiResponse, Client, PaginatedResponse } from '../types';

export interface ClientFilters { search?: string; status?: string; gender?: string; leadSource?:string; membershipStatus?:string; from?:string; to?:string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc'|'desc'; }
export interface PageMeta { page:number; limit:number; total:number; totalPages:number; }
export interface Client360Params { appointmentPage?:number; invoicePage?:number; membershipPage?:number; pointsPage?:number; walletPage?:number; feedbackPage?:number; limit?:number; }
export interface Client360Data {
  client:Client; appointments:any[]; invoices:any[]; memberships:any[]; pointsHistory:any[]; walletHistory:any[]; feedback:any[];
  pagination:Record<'appointments'|'invoices'|'memberships'|'points'|'wallet'|'feedback',PageMeta>;
}
export interface CreateClientDto {
  firstName: string; lastName?: string; mobile: string; email?: string; gender?: string;
  dateOfBirth?: string; anniversary?: string; address?: string; city?: string; gstin?: string;
  leadSource?: string; sourceDescription?: string; referredBy?: string;
  marketing?:{sms:boolean;email:boolean;whatsapp:boolean};
  status?: 'Active' | 'Inactive';
}
export interface ClientStats {
  total:number; active:number; inactive:number; recent:number;lost:number;members:number;clients:number;contacts:number;
  breakdown:{ gender:Array<{_id:string;count:number}>; tier:Array<{_id:string;count:number}>; leadSource:Array<{_id:string;count:number}> };
  financial:{ totalSpend:number;walletBalance:number;rewardPoints:number;averageSpend:number;averageVisits:number };
  topClients:Array<Pick<Client,'_id'|'firstName'|'lastName'|'mobile'|'totalSpend'|'totalVisits'>>;
}
export interface DuplicateGroup { _id:string; count:number; clients:Array<Pick<Client,'_id'|'code'|'firstName'|'lastName'|'mobile'|'email'|'totalSpend'>>; }

const clientService = {
  getAll:     (f: ClientFilters = {}) => api.get<PaginatedResponse<Client>>('/clients', { params: f }).then(r => r.data),
  search:     (q: string)             => api.get<ApiResponse<Client[]>>('/clients/search', { params: { q } }).then(r => r.data),
  getById:    (id: string)            => api.get<ApiResponse<Client>>(`/clients/${id}`).then(r => r.data),
  create:     (data: CreateClientDto) => api.post<ApiResponse<Client>>('/clients', data).then(r => r.data),
  update:     (id: string, data: Partial<CreateClientDto>) => api.put<ApiResponse<Client>>(`/clients/${id}`, data).then(r => r.data),
  deactivate: (id: string)            => api.delete<ApiResponse>(`/clients/${id}`).then(r => r.data),
  activate:   (id: string)            => api.post<ApiResponse<Client>>(`/clients/${id}/activate`).then(r => r.data),
  checkMobile: (mobile: string, excludeId?: string) =>
    api.get<ApiResponse<{ exists: boolean }>>('/clients/check-mobile', { params: { mobile, excludeId } }).then(r => r.data),
  getWallet:  (id: string)            => api.get<ApiResponse<Client>>(`/clients/${id}/wallet`).then(r => r.data),
  topUpWallet:(id: string, amount: number) => api.post<ApiResponse<Client>>(`/clients/${id}/wallet/topup`, { amount }).then(r => r.data),
  debitWallet:(id: string, amount: number, reason: string) => api.post<ApiResponse<Client>>(`/clients/${id}/wallet/debit`, { amount, reason }).then(r => r.data),
  redeemPoints:(id: string, points: number) => api.post<ApiResponse<Client>>(`/clients/${id}/points/redeem`, { points }).then(r => r.data),
  getStats:   () => api.get<ApiResponse<ClientStats>>('/clients/stats').then(r => r.data),
  getDuplicates: () => api.get<ApiResponse<DuplicateGroup[]>>('/clients/duplicates').then(r=>r.data),
  merge: (targetId:string,sourceIds:string[]) => api.post<ApiResponse<Client>>('/clients/merge',{targetId,sourceIds}).then(r=>r.data),
  exportCsv: () => api.get<Blob>('/clients/export',{responseType:'blob'}),
  importCsv: async (file:File) => {
    let uploadFile=file;
    if (/\.(xlsx|xls)$/i.test(file.name)) {
      const XLSX=await import('xlsx');
      const workbook=XLSX.read(await file.arrayBuffer(),{type:'array'});
      const firstSheet=workbook.Sheets[workbook.SheetNames[0]];
      if (!firstSheet) throw new Error('The Excel workbook does not contain a worksheet.');
      const csv=XLSX.utils.sheet_to_csv(firstSheet);
      uploadFile=new File([csv],file.name.replace(/\.(xlsx|xls)$/i,'.csv'),{type:'text/csv'});
    }
    const body=new FormData();body.append('file',uploadFile);
    return api.post<ApiResponse<{created:number;updated:number;skipped:number;errors:string[]}>>('/clients/import',body).then(r=>r.data);
  },
  get360:     (id: string, params:Client360Params={}) => api.get<ApiResponse<Client360Data>>(`/clients/${id}/360`,{params}).then(r => r.data),
  addNote:    (id:string,text:string)=>api.post<ApiResponse<Client>>(`/clients/${id}/notes`,{text}).then(r=>r.data),
};

export default clientService;
